import { LitElement, html, css, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { AuthService } from '../services/auth-service';
import { router } from '../router';
import '../components/modal-component'; // Import the modal component
import { cachedFetch } from '../utils/cachedFetch.js';

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

@customElement('app-home')
export class AppHome extends LitElement {
  @state() private currentDate: Date = new Date();
  @state() private schedule: { Date: string; Shift: string; Color: string; ModifiedByUser: boolean;IsSpecial:boolean}[] = [];
  @state() private modalOpen: boolean = false;

  private workerId: string | null = localStorage.getItem('workerNumber');
  private selectedTeam: string = localStorage.getItem('selectedTeam') || 'T1';
@state() private teamScheduleGenerated: boolean = false;
@state() private shiftWorkers: { name: string; shift: string; color: string | null }[] = [];
@state() private availableWorkers: { name: string; shift: string; color: string | null }[] = [];
@state() private holidays: string[] = []; // Store the holidays
private featureModalOpen = false; // For the floating button modal
private workerSelectionModalOpen = false;
@state() private selectedWorkersSchedules: { date: string, shiftType: string, workerNumber: string, color: string }[] = [];
@state() private selectedWorkers: string[] = [];

 private selectedColor = '';
 private selectedShift = '';
 private selectedDate = '';
private isSpecialNight = false;
private modifiedByUser = false;

static styles = css`
:root {
      --color-bht: red;   /* For example */
  --color-ext: yellow;
  --color-gen: blue;
  --color-default: gray;
  --color-off: #bdfb53;
}
 :host {
  display: block;
  font-family: 'Arial', sans-serif;
  text-align: center;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background: var(--background-color);
  color: var(--text-color);
}

header {
  background-color: var(--header-background);
  color: var(--header-text);
  padding: 16px;
  text-align: center;
  font-size: 1.5rem;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.calendar-container {
  position: relative;
  background: var(--calendar-background);
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0 4px 20px var(--box-shadow-color);
  width: 90%;
  max-width: 900px;
  margin: auto;
  z-index: 1;

  /* Default max height for larger screens */
  max-height: calc(100vh - 100px); /* Adjust this based on your bottom nav bar height */
  overflow-y: auto; /* Enables vertical scrolling */
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.calendar-header button {
  background: var(--button-background);
  color: var(--button-text);
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
}

.calendar-header button:hover {
  background: var(--button-hover);
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
}

.day-name {
  font-weight: bold;
  color: var(--day-name-color);
}

.calendar-day {
  background: var(--day-background);
  text-align: center;
  padding: 16px;
  border-radius: 5px;
  box-shadow: 0 1px 3px var(--box-shadow-color);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.calendar-day.today {
  background: var(--today-background);
  color: var(--today-text);
}

.calendar-day .day-number {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 8px;
}

.event {
  padding: 4px;
  border-radius: 3px;
  font-size: 0.8rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  margin-top: 4px;
  position: relative; /* Needed for the bottom bar to be positioned correctly */
}


.event.placeholder {
  visibility: hidden;
  height: 17px;
}

/* Bottom Bar (only when hasBottomBar is true) */
.has-bottom-bar::after {
  content: "";
  display: block;
  height: 4px;
  width: 100%;
  background-color: var(--shift-color, transparent); /* This should now work */
  position: absolute;
  bottom: 0;
  left: 0;
  border-radius: 0 0 3px 3px;
}






.color-bht {
  background-color: red;
  color: white;
}

.color-ext {
  background-color: yellow;
  color: black;
}

.color-gen {
  background-color: blue;
  color: white;
}

.color-default {
  background-color: gray;
  color: white;
}
.color-off {
  background-color: #bdfb53;
  color: black;
}

.dashed-border {
  border: 2px dashed #ff9800; /* Orange dashed border */
}

.footer {
  margin-top: 24px;
  font-size: 0.8rem;
  color: var(--footer-color);
}

.footer a {
  color: var(--footer-link-color);
  text-decoration: none;
}

.footer a:hover {
  text-decoration: underline;
}

.calendar-footer {
  margin-top: 16px;
  font-size: 0.9rem;
  color: #999;
  text-align: left;
  padding: 8px;
  border-top: 1px solid #ddd;
}

/* Light Mode */
@media (prefers-color-scheme: light) {
  :host {
    --background-color: #f3f3f3;
    --text-color: #333333;
    --header-background: #003366;
    --header-text: white;
    --calendar-background: #ffffff;
    --box-shadow-color: rgba(0, 0, 0, 0.1);
    --button-background: #003366;
    --button-text: #ffffff;
    --button-hover: #00509e;
    --day-name-color: #003366;
    --day-background: #f0f0f0;
    --today-background: #003366;
    --today-text: #ffffff;
    --footer-color: #aaa;
    --footer-link-color: #00509e;
  }
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  :host {
    --background-color: #181818;
    --text-color: #e0e0e0;
    --header-background: #1e1e1e;
    --header-text: #ffffff;
    --calendar-background: #1e1e1e;
    --box-shadow-color: rgba(255, 255, 255, 0.1);
    --button-background: #333333;
    --button-text: #ffffff;
    --button-hover: #555555;
    --day-name-color: #aaaaaa;
    --day-background: #2c2c2c;
    --today-background: #00509e;
    --today-text: #ffffff;
    --footer-color: #888888;
    --footer-link-color: #4a90e2;
  }
}
/* Default desktop view */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr); /* 7 equal-width columns */
  gap: 8px; /* Default gap between cells */
}


.calendar-grid {
    grid-template-columns: repeat(7, 1fr); /* Still 7 columns */
    gap: 4px; /* Even smaller gap */
  }

  .calendar-day {
    padding: 10px; /* Slightly reduced padding */
    font-size: 0.9rem; /* Smaller font size */
  }

  .calendar-header {
    font-size: 1rem; /* Further reduce header font size */
  }
.calendar-day.holiday {
  background-color: #634b4a;
  color: white; /* Adjust text color for better contrast */
}

.floating-button {
  position: fixed;
  bottom: 80px;
  right: 16px;
  width: 56px;
  height: 56px;
  background-color: #0c3363;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4); /* Semi-transparent backdrop */
  display: flex;
  align-items: flex-end; /* Ensures the modal is near the button */
  justify-content: flex-end;
  z-index: 1000;
}

.feature-modal {
  background: white;
  border: 1px solid #ccc;
  padding: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  z-index: 1001;
  width: 200px;
  margin: 16px; /* Adds space around the modal */
}

.feature-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.feature-modal .close-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
}

.feature-modal .modal-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feature-modal button {
  padding: 8px 12px;
  background-color: #0c3363;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.feature-modal button:hover {
  background-color: #0056b3;
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Modal Box */
.worker-selection-modal {
  position: relative;
  background-color: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 350px;
  padding: 20px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 40vh;
  animation: fadeIn 0.3s ease-out;
}

/* Title */
.worker-selection-modal h4 {
  margin: 0;
  font-size: 1.8rem;
  color: #333;
  font-weight: 600;
  text-align: center;
  margin-bottom: 15px;
}

/* Worker List */
.worker-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 5px 0;
  border-bottom: 1px solid #eee;
}

.worker-item:last-child {
  border-bottom: none;
}

.worker-item input {
  margin-right: 15px;
  transform: scale(1.2);
}

.worker-item label {
  font-size: 1.2rem;
  color: #444;
  cursor: pointer;
}

/* Close Button */
.worker-selection-modal .close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: transparent;
  border: none;
  color: #888;
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.3s;
}


.color-blue-a {
  background-color: #85C1E9; /* Light Blue */
  color: #ffffff; /* Text color */
}

.color-blue-b {
  background-color: #2874A6; /* Darker Blue */
  color: #ffffff; /* Text color */
}



/* Responsive design */
@media (max-width: 600px) {
  .worker-selection-modal {
    width: 95%;
    padding: 15px;
  }

  .worker-selection-modal h4 {
    font-size: 1.6rem;
  }

  .worker-item label {
    font-size: 1rem;
  }

  .worker-item input {
    transform: scale(1);
  }
  .calendar-grid {
    grid-template-columns: repeat(7, 1fr); /* Still maintain 7 columns */
    gap: 6px; /* Slightly smaller gap */
  }

  .calendar-day {
    padding: 8px; /* Adjust day padding for smaller screens */
    font-size: 0.75rem; /* Adjust font size */
  }

  .calendar-header {
    font-size: 1rem; /* Adjust header font size */
  }
}

@media (max-width: 375px) {

  .event {
  padding: 3px;
  border-radius: 3px;
  font-size: 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 90%;
  margin-top: 4px;
  position: relative; /* Needed for the bottom bar to be positioned correctly */
}


.event.placeholder {
  visibility: hidden;
  height: 11px;
}


/* Bottom Bar (only when hasBottomBar is true) */
.has-bottom-bar::after {
  content: "";
  display: block;
  height: 4px;
  width: 100%;
  background-color: var(--shift-color, transparent); /* This should now work */
  position: absolute;
  bottom: 0;
  left: 0;
  border-radius: 0 0 3px 3px;
}

  .worker-selection-modal {
    width: 95%;
    padding: 15px;
  }

  .worker-selection-modal h4 {
    font-size: 1.6rem;
  }

  .worker-item label {
    font-size: 1rem;
  }

  .worker-item input {
    transform: scale(1);
  }
  .calendar-grid {
    grid-template-columns: repeat(7, 1fr); /* Still maintain 7 columns */
    gap: 3px; /* Slightly smaller gap */
  }

  .calendar-day {
    padding: 4px; /* Adjust day padding for smaller screens */
    font-size: 0.7rem; /* Adjust font size */
  }

  .calendar-header {
    font-size: 1rem; /* Adjust header font size */
  }


.calendar-container {
  position: relative;
  background: var(--calendar-background);
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0 4px 20px var(--box-shadow-color);
  width: 90%;
  max-width: 900px;
  margin: auto;
  z-index: 1;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
}

.calendar-header button {
  background: var(--button-background);
  color: var(--button-text);
  border: none;
  padding: 7px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.3s ease;
}

}

/* Modal Fade-in Animation */
@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }


}


`;




connectedCallback() {
  super.connectedCallback();
  if (!this.workerId) {
    console.error("Worker ID is not available.");
    router.navigate('');
  }
      this.fetchHolidays(); // Fetch holidays when the component loads
  const month = this.currentDate.getMonth() + 1;
  const year = this.currentDate.getFullYear();

  this.fetchSchedule(month, year);

this.addEventListener('refresh-calendar' as string, this.handleRefreshCalendar);

}

disconnectedCallback() {
  // Clean up event listener when the component is disconnected
this.removeEventListener('refresh-calendar' as string, this.handleRefreshCalendar);
}

handleRefreshCalendar() {
  // Call the renderCalendar function to refresh the calendar
    const month = this.currentDate.getMonth() + 1;
  const year = this.currentDate.getFullYear();
  this.fetchSchedule(month, year);
   this.fetchAndDisplaySchedules(month, year);
  this.renderCalendar();
}

 private changeMonth(offset: number) {
  // Change the month based on the offset
    this.currentDate.setDate(1);
  this.currentDate.setMonth(this.currentDate.getMonth() + offset);

  const month = this.currentDate.getMonth() + 1;
  const year = this.currentDate.getFullYear();
  this.fetchSchedule(month, year);
  this.fetchAndDisplaySchedules(month, year);
}


  private async fetchAvailableWorkers(day: number) {
  const month = this.currentDate.getMonth() + 1;
  const year = this.currentDate.getFullYear();

  try {
    const response = await cachedFetch(
      `${apiServerUrl}/available-workers?day=${day}&month=${month}&year=${year}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
        },
      }
    );
    const data = await response.json();

    if (response.ok) {
      // Map the data into structured objects for use in the modal
      this.availableWorkers = data.availableWorkers.map(
        (worker: { workerName: string; shift: string; color?: string }) => ({
          name: worker.workerName,
          shift: worker.shift,
          color: worker.color || null, // Default to null if color is not provided
        })
      );
    } else {
      console.error('Error fetching available workers:', data.message || 'Unknown error');
    }
  } catch (error) {
    console.error('Error fetching available workers:', error);
  }
}

private async fetchShiftWorkers(day: number, shift: string) {
  const month = this.currentDate.getMonth() + 1;
  const year = this.currentDate.getFullYear();

  try {
    const response = await cachedFetch(
      `${apiServerUrl}/shift-workers?day=${day}&month=${month}&year=${year}&shift=${shift}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
        },
      }
    );
    const data = await response.json();

    if (response.ok) {
      // Map the data into structured objects for use in the modal
      this.shiftWorkers = data.shiftWorkers.map(
        (worker: { workerName: string; shift: string; color?: string }) => ({
          name: worker.workerName,
          shift: worker.shift,
          color: worker.color || null, // Default to null if color is not provided
        })
      );
    } else {
      console.error('Error fetching shift workers:', data.message || 'Unknown error');
    }
  } catch (error) {
    console.error('Error fetching shift workers:', error);
  }
}


private async handleEventClick(day: number, shift: string) {
  await this.fetchAvailableWorkers(day);  // Fetch the available workers
  if (shift && shift == 'S' || shift == 'D'|| shift == 'N' || shift == 'T' || shift == 'HR') {
    await this.fetchShiftWorkers(day, shift);    // Fetch the workers for this shift
  }
  this.modalOpen = true; // Open the modal again
  this.requestUpdate();  // Request an update to ensure the modal is rerendered

}

private async handleDayClick(day: number) {
    this.shiftWorkers = [];
  await this.fetchAvailableWorkers(day);  // Fetch the available workers
  this.modalOpen = true; // Open the modal again
  this.requestUpdate();  // Request an update to ensure the modal is rerendered

}


private closeModal() {
  this.modalOpen = false;
  this.availableWorkers = [];  // Optionally clear the workers list as well
  this.requestUpdate();  // Ensures that the component is re-rendered
}



private renderModal() {
  // Ensure this renders properly when modalOpen is true
  return this.modalOpen ? html`
<modal-component .open=${this.modalOpen} .workers=${this.availableWorkers} @close=${this.closeModal}
.myShiftWorkers=${this.shiftWorkers} .selectedShift=${this.selectedShift} .selectedColor=${this.selectedColor}
.selectedDate=${this.selectedDate} .teamScheduleGenerated=${this.teamScheduleGenerated} .isSpecialNight=${this.isSpecialNight} .ModifiedByUser=${this.modifiedByUser}> </modal-component>
  ` : null;
}




 async fetchSchedule(month: number, year: number) {
  try {
    const response = await cachedFetch(`${apiServerUrl}/schedule/${this.workerId}?month=${month}&year=${year}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getToken()}`,
      },
    });
    const data = await response.json();

    if (response.ok) {
      // If schedule is empty, handle accordingly
      if (data.schedule.length > 0) {
        this.schedule = data.schedule.map((event: { Date: string; Shift: string }) => ({
          ...event,
          Date: new Date(event.Date).toISOString().split('T')[0],
        }));
        this.teamScheduleGenerated = false;
      } else {
        // If no schedule, handle appropriately (you can set an empty array or show a message)
        this.schedule = [];
        this.teamScheduleGenerated = true; // or handle this as per your logic
        this.fetchTeamSchedule(month, year);
      }
    } else {
      this.fetchTeamSchedule(month, year);
    }
  } catch (error) {
    console.error("Error fetching schedule:", error);
    alert("An error occurred while fetching the schedule. Please try again later.");
  }
}


  private async fetchTeamSchedule(month: number, year: number) {
    try {
      const teamResponse = await cachedFetch(`${apiServerUrl}/schedule-for-team?workerId=${this.workerId}&team=${this.selectedTeam}&month=${month}&year=${year}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
        },
      });
      const teamData = await teamResponse.json();

      if (teamResponse.ok) {
        this.schedule = teamData.schedule.map((event: { Date: string; Shift: string }) => ({
          ...event,
          Date: new Date(event.Date).toISOString().split('T')[0],
        }));
        this.teamScheduleGenerated = true;
      } else {
        this.schedule = [];
        this.teamScheduleGenerated = false;
      }
    } catch (error) {
      console.error("Error fetching team schedule:", error);
    }
  }

private renderCalendar() {
  // Calculate the day index for the first day of the month
  const firstDayOfMonth = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth(),
    1
  ).getDay();
  // Adjust for week starting on Monday:
  // If firstDayOfMonth is 0 (Sunday), then treat it as 6. Otherwise, subtract 1.
  const firstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const daysInMonth = new Date(
    this.currentDate.getFullYear(),
    this.currentDate.getMonth() + 1,
    0
  ).getDate();
  const today = new Date();
  const days: TemplateResult[] = [];

  // Define colors for workers
  const colors = ['color-blue-a', 'color-blue-b']; // Shades of blue for workers


  // Add empty divs for days before the first day of the current month
  for (let i = 0; i < firstDay; i++) {
    days.push(html`<div class="calendar-day"></div>`);
  }

  // Render the days of the current month
  for (let day = 1; day <= daysInMonth; day++) {
    const isToday =
      today.getFullYear() === this.currentDate.getFullYear() &&
      today.getMonth() === this.currentDate.getMonth() &&
      today.getDate() === day;

    const dateString = `${this.currentDate.getFullYear()}-${String(
      this.currentDate.getMonth() + 1
    ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isHoliday = this.holidays.includes(dateString);

    // Main schedule events (filtering out `DC` and `DS`)
    const mainEvents = this.schedule.filter(
      event => event.Date === dateString && event.Shift !== 'DC' && event.Shift !== 'DS');

    // other Workers schedules with placeholders
    const workerSchedules = this.selectedWorkers.map((workerNumber, index) => {
      const workerEvent = this.selectedWorkersSchedules.find(
        schedule =>
          schedule.date === dateString &&
          schedule.workerNumber === workerNumber &&
          (schedule.shiftType == 'S' ||
            schedule.shiftType == 'D' ||
            schedule.shiftType == 'N' ||
            schedule.shiftType == 'HR-35' ||
            schedule.shiftType == 'T'||
            schedule.shiftType == 'ab')
      );

      if (workerEvent) {
        return html`
<div class="event ${colors[index % colors.length]} ${(workerEvent.color !== 'N/A' && workerEvent.color !== 'GRY') ? 'has-bottom-bar' : ''}"
     style="${workerEvent.color !== 'N/A' ? '--shift-color: ' + this.getColorClassSpagueti(workerEvent.color) : ''}">
  ${workerEvent.shiftType}
</div>


        `;
      } else {
        return html`
          <div class="event placeholder ${colors[index % colors.length]}"></div>
        `;
      }
    });

    // Add placeholders for main events if none exist
const mainEventElements =
  mainEvents.length > 0
    ? mainEvents.map(
        event => html`
          <div class="event ${this.getColorClass(event.Color)} ${event.ModifiedByUser ? 'dashed-border' : ''}">
            ${(event.Shift === "S" || event.Shift === "D") && event.IsSpecial ? `${event.Shift}*` : event.Shift}
          </div>
        `
      )
    : html`<div class="event placeholder"></div>`;

    // Handle click event
    const handleClick = () => {
      if (mainEvents.length === 0) {
        this.handleDayClick(day);
        const month = this.currentDate.getMonth() + 1;
        const year = this.currentDate.getFullYear();
        this.selectedDate = new Date(year, month - 1, day).toISOString(); // Convert Date to string
      } else {
        this.handleEventClick(day, mainEvents[0].Shift);
        this.selectedColor = mainEvents[0].Color;
        this.selectedShift = mainEvents[0].Shift;
        this.selectedDate = mainEvents[0].Date;
        this.isSpecialNight = mainEvents[0].IsSpecial;
        this.modifiedByUser = mainEvents[0].ModifiedByUser;
      }
    };

    // Render the day
    days.push(html`
      <div class="calendar-day ${isToday ? 'today' : ''} ${isHoliday ? 'holiday' : ''}" @click=${handleClick}>
        ${day}

        <!-- Render main schedule events or placeholders -->
        ${mainEventElements}

        <!-- Render worker schedules or placeholders -->
        ${workerSchedules}
      </div>
    `);
  }

  return days;
}




  private getColorClass(color: string) {
    switch (color) {
      case 'BHT': return 'color-bht';
      case 'EXT': return 'color-ext';
      case 'GEN': return 'color-gen';
      case 'OFF': return 'color-off';
      default: return 'color-default';
    }
  }
  private getColorClassSpagueti(color: string) {
    switch (color) {
      case 'BHT': return 'red';
      case 'EXT': return 'yellow';
      case 'GEN': return 'blue';
      case 'OFF': return '#bdfb53';
      default: return 'gray';
    }
  }

private async fetchHolidays() {
  try {
    const response = await cachedFetch(`${apiServerUrl}/holidays`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${AuthService.getToken()}`,
      },
    });

    const data = await response.json();
    // Check if the data is valid and contains the holiday array
    if (response.ok && Array.isArray(data)) {
      // Save the holiday dates as UTC without the time part
      this.holidays = data.map((item: { holiday_date: string }) => {
        const holidayDate = new Date(item.holiday_date); // Convert to UTC date
        return holidayDate.toISOString().split('T')[0];  // Extract the date part only (YYYY-MM-DD)
      });

    } else {
      console.error('Error: Invalid response structure or no holiday data found');
    }
  } catch (error) {
    console.error('Error fetching holidays:', error);
  }
}


private openFeatureModal() {
  this.featureModalOpen = true;
  this.requestUpdate();
}

private closeFeatureModal() {
  this.featureModalOpen = false;
  this.requestUpdate();
}

private openWorkerSelectionModal() {
  this.workerSelectionModalOpen = true;
  this.requestUpdate();
}

private closeWorkerSelectionModal() {
      const month = this.currentDate.getMonth() + 1;
  const year = this.currentDate.getFullYear();
  this.workerSelectionModalOpen = false;
  this.fetchAndDisplaySchedules(month, year);
  if(this.selectedWorkers.length==0){
      this.selectedWorkersSchedules = [] }
  this.requestUpdate();
}

private renderFeatureModal() {
  return this.featureModalOpen ? html`
    <div class="modal-overlay" @click=${this.closeFeatureModal}>
      <div class="feature-modal" @click=${(e: Event) => e.stopPropagation()}>
        <div class="modal-body">
          <button @click=${this.handleOverlaySchedules}>Overlay Colleague Schedules</button>
          <button @click=${this.handleExportCalendar}>Export Calendar</button>
        </div>
      </div>
    </div>
  ` : null;
}

private handleExportCalendar() {
  this.featureModalOpen = false; // Close the modal
  // Assuming you have the worker number or some identifier to locate the ICS file
  const workerNumber = this.workerId; // Get the current worker's number or identifier

  // Create the webcal URL for subscribing to the calendar
  const webcalUrl = `webcal://filoapp.xyz/ics_calendars/${workerNumber}.ics`;

  // Create a link element for subscribing to the calendar
  const link = document.createElement('a');
  link.href = webcalUrl; // Set the webcal URL
  link.textContent = "Subscribe to Calendar"; // Optional text for the link

  // Optionally, you could open the link in a new tab or handle the subscription further
  window.open(link.href, '_blank'); // Open the calendar subscription link in a new tab

  // You can also append the link to the document if needed, e.g., for the user to click
  document.body.appendChild(link);
  link.click(); // Trigger the link click
}


private handleOverlaySchedules() {
  this.featureModalOpen = false; // Close the modal

  // Implement overlay logic
  this.fetchWorkers();
  this.openWorkerSelectionModal();
  this.requestUpdate();
  //Pode ser feito só quando a lista está vazia

  }


async fetchWorkers() {
  try {
    const response = await cachedFetch(`${apiServerUrl}/workers`, {
      headers: {
        'Authorization': `Bearer ${AuthService.getToken()}` // Assuming `this.token` stores the auth token
      }
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const workers = await response.json();
    this.availableWorkers = workers.map((worker: any) => ({
      workerNumber: worker.WorkerNumber,
      name: worker.Name
    })); // Transform API data into the format needed for the modal
  } catch (error) {
    console.error('Failed to fetch workers:', error);
  }
}

private renderWorkerSelectionModal() {
  return this.workerSelectionModalOpen ? html`
    <div class="modal-overlay" @click=${this.closeWorkerSelectionModal}>
      <div class="worker-selection-modal" @click=${(e: Event) => e.stopPropagation()}>
        <div class="modal-header">
          <button class="close-button" @click=${this.closeWorkerSelectionModal}>X</button>
          <h4>Select Colleagues</h4>
        </div>
        <div class="modal-body">
          ${this.availableWorkers.length > 0 ? this.availableWorkers.map((worker: any) => html`
            <div class="worker-item">
             <input
  type="checkbox"
  id="worker-${worker.workerNumber}"
  .value=${worker.workerNumber}
  .checked=${this.selectedWorkers.includes(worker.workerNumber)}
  @change=${() => this.toggleWorkerSelection(worker.workerNumber)}
  ?disabled=${this.selectedWorkers.length >= 2 && !this.selectedWorkers.includes(worker.workerNumber)}>
<label for="worker-${worker.workerNumber}">${worker.name}</label>

            </div>
          `) : html`<p>Loading workers...</p>`}
        </div>
      </div>
    </div>
  ` : null;
}
private toggleWorkerSelection(workerNumber: string) {
  const index = this.selectedWorkers.indexOf(workerNumber);

  if (index === -1) {
    // Worker not selected, so check if the limit of 2 is reached
    if (this.selectedWorkers.length < 2) {
      this.selectedWorkers.push(workerNumber);
    } else {
      // Optionally, you could show a message or visual cue to indicate the limit
      alert("You can only select up to 2 workers.");
    }
  } else {
    // Worker already selected, so remove from the list
this.selectedWorkers = this.selectedWorkers.filter(worker => worker !== workerNumber);
  }

}


private async fetchAndDisplaySchedules(month: number, year: number) {
  const workerNumbers = this.selectedWorkers.join(',');
  if (workerNumbers.length === 0) return;


  try {
    const response = await cachedFetch(`${apiServerUrl}/workers/${workerNumbers}/schedule?month=${month}&year=${year}`, {
      headers: {
        'Authorization': `Bearer ${AuthService.getToken()}`
      }
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const schedules = await response.json();

    // Store the worker schedules in the array
    this.selectedWorkersSchedules = schedules.map((schedule: any) => ({
      date: new Date(schedule.Date).toISOString().split('T')[0],
      shiftType: schedule.Shift,
      workerNumber: schedule.workerNumber,
      color: schedule.Color
    }));
  } catch (error) {
    console.error('Error fetching schedules for selected workers:', error);
  }
}




  render() {
    const monthYear = this.currentDate.toLocaleDateString('default', { month: 'long', year: 'numeric' });
    const noScheduleMessage = this.teamScheduleGenerated
      ? `No individual schedule found for ${monthYear}. Team schedule has been generated for ${this.selectedTeam}.`
      : '';

    return html`
      <app-header></app-header>
      <div class="calendar-container">
        <div class="calendar-header">
          <button @click=${() => this.changeMonth(-1)}>&lt; Previous</button>
          <h2>${monthYear}</h2>
          <button @click=${() => this.changeMonth(1)}>Next &gt;</button>
        </div>
<div class="calendar-grid">
  <div class="calendar-day day-name">Mon</div>
  <div class="calendar-day day-name">Tue</div>
  <div class="calendar-day day-name">Wed</div>
  <div class="calendar-day day-name">Thu</div>
  <div class="calendar-day day-name">Fri</div>
  <div class="calendar-day day-name">Sat</div>
  <div class="calendar-day day-name">Sun</div>
  ${this.renderCalendar()}
</div>

        ${noScheduleMessage ? html`<div class="calendar-footer">${noScheduleMessage}</div>` : ''}
      </div>
  ${this.modalOpen ? this.renderModal() : ''}
        <div class="floating-button" @click=${this.openFeatureModal}>
      +
    </div>
          ${this.renderFeatureModal()} <!-- Feature modal -->
           ${this.renderWorkerSelectionModal()}


  `;

  }
}
